import { useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown, DropdownMenu, DropdownToggle } from "reactstrap";
import { Button, SearchBar } from "shared/ui-basics";
import { FilterButton } from "./FilterButton";
import { FilterOption } from "./FilterOption";
import styled from "styled-components";

interface FilterTemplateProps {
	innerRef: React.RefObject<{
		getFilters: () => string[];
	}>;
	records: any[];
	title: string;
	icon: string;
	idAttribute?: string;
	handleApply: () => void;
}

export const FilterTemplate: React.FC<FilterTemplateProps> = ({ innerRef, records, title, icon, idAttribute = "id", handleApply }) => {
	const { t } = useTranslation();

	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const [memoIds, setMemoIds] = useState<string[]>([]);
	const [searchedValue, setSearchedValue] = useState<string>("");
	const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

	const toggle = ({ isApplyOrClear }: { isApplyOrClear: boolean }): void => {
		if (isApplyOrClear) setMemoIds(selectedIds);
		else setSelectedIds(memoIds);

		setDropdownOpen((prevState) => !prevState);
	};

	const handleSearch = (e: any) => {
		const value = e.target.value;
		setSearchedValue(value);
	};

	const filteredRecords = records?.filter((rec) => rec?.name?.toLowerCase()?.includes(searchedValue));

	const handleSelect = (id: string) => {
		if (!selectedIds?.includes(id)) setSelectedIds([...selectedIds, id]);
		else setSelectedIds(selectedIds.filter((sId) => sId !== id));
	};

	const handleClear = () => {
		toggle({ isApplyOrClear: true });
		setSelectedIds([]);
		setTimeout(handleApply);
	};

	const apply = () => {
		toggle({ isApplyOrClear: true });
		handleApply();
	};

	useImperativeHandle(innerRef, () => ({
		getFilters: () => selectedIds,
	}));

	return (
		<FilterCustomDropdown isOpen={dropdownOpen} toggle={toggle}>
			<CustomToggle>
				<FilterButton title={title} selectedIds={selectedIds} icon={icon} />
			</CustomToggle>

			<DropdownMenu>
				<MenuContainer>
					<SearchBar globalFilterValue={searchedValue} handleChange={handleSearch} height={34} />
					<ListContainer>
						{filteredRecords?.map((rec) => (
							<div key={rec?.[idAttribute]} onClick={() => handleSelect(rec?.[idAttribute])}>
								<FilterOption isSelected={selectedIds?.includes(rec?.[idAttribute])} item={rec} />
							</div>
						))}
					</ListContainer>
					<Footer>
						<Button variant="dark-outline" size="s" onClick={handleClear}>
							{t("CLEAR")}
						</Button>
						<Button variant="dark" size="s" onClick={apply}>
							{t("APPLY")}
						</Button>
					</Footer>
				</MenuContainer>
			</DropdownMenu>
		</FilterCustomDropdown>
	);
};

const FilterCustomDropdown = styled(Dropdown)`
	padding: 0px;
	.dropdown-menu.show {
		padding: 0px;
	}
`;

const CustomToggle = styled(DropdownToggle)`
	padding: 0px;
`;

const MenuContainer = styled.div`
	width: 180px;
	padding: 8px;
	display: flex;
	flex-direction: column;
`;

const ListContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	min-height: 56px;
	max-height: 360px;
	overflow: scroll;
	margin-top: 16px;
`;

const Footer = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
	padding: 8px 8px 0px 8px;
	border-top: 1px solid #d9d9d9;
`;
